/**
 * QuickActionsBar - Universal quick actions for card details panels
 *
 * Provides Star, Add to List, Add to Deck, and Add to Collection actions in a consistent bar.
 * Used across collection, lists, starred, set details, and deck editor card details panels.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { IoAddCircleOutline, IoStar, IoStarOutline } from 'react-icons/io5';

import AddToCollectionModal from '@/features/collection/components/AddToCollectionModal';
import AddToListMenu from '@/features/lists/components/AddToListMenu';
import { useIsStarred, useToggleStar } from '@/features/starred/hooks/api';
import useCardLookup from '@/hooks/useCardLookup';
import type { MinimalCard } from '@/services/cardDatabase';
import type { ScryfallCard } from '@/types/card';

import AddToDeckMenu from './AddToDeckMenu';

// ============================================================================
// Types
// ============================================================================

export interface QuickActionsBarProps {
  /** Oracle ID for starred and list operations */
  oracleId: string | null;
  /** Card name for display and list operations */
  cardName: string;
  /** Full Scryfall card data (needed for deck operations) */
  card: ScryfallCard | null;
  /** Current scryfall_id for star toggle and collection operations */
  scryfallId?: string;
  /** Whether to hide the star action (e.g., already shown in header) */
  hideStar?: boolean;
  /** Whether to hide the list action (e.g., already shown in header) */
  hideList?: boolean;
  /** Whether to hide the deck action */
  hideDeck?: boolean;
  /** Whether to hide the collection action (e.g., when editing existing collection card) */
  hideCollection?: boolean;
  /** Set code for per-set folder persistence (used in Set Details) */
  setCode?: string | null;
  /** Additional className for the container */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function QuickActionsBar({
  oracleId,
  cardName,
  card,
  scryfallId,
  hideStar = false,
  hideList = false,
  hideDeck = false,
  hideCollection = false,
  setCode,
  className,
}: QuickActionsBarProps): ReactElement | null {
  // Starred functionality
  const isStarred = useIsStarred(oracleId ?? undefined);
  const toggleStar = useToggleStar();

  // Add to Collection modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Get minimal card for the modal
  const { cardIndexById } = useCardLookup();
  const minimalCard: MinimalCard | null = scryfallId ? (cardIndexById.get(scryfallId) ?? null) : null;

  // Handle open Add to Collection modal
  const handleOpenAddModal = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  // Handle close Add to Collection modal
  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  // Don't render if all actions are hidden
  if (hideStar && hideList && hideDeck && hideCollection) return null;

  // Track which actions are visible
  const visibleActions = [!hideStar, !hideList, !hideDeck, !hideCollection].filter(Boolean);
  const visibleCount = visibleActions.length;

  return (
    <>
      <section className={clsx('border-storm border-b px-4 py-3', className)}>
        <div className="flex items-center justify-center gap-2">
          {/* Star action */}
          {!hideStar && (
            <button
              onClick={() => oracleId && toggleStar(oracleId, cardName, scryfallId)}
              className={clsx(
                'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors',
                isStarred ? 'text-gold hover:bg-gold/10' : 'text-silver hover:bg-slate hover:text-pearl',
              )}
              aria-label={isStarred ? 'Remove from starred' : 'Add to starred'}
            >
              {isStarred ? <IoStar className="h-4 w-4" /> : <IoStarOutline className="h-4 w-4" />}
              Star
            </button>
          )}

          {/* Divider after Star */}
          {!hideStar && visibleCount > 1 && <div className="bg-storm h-6 w-px" />}

          {/* Add to List action */}
          {!hideList && <AddToListMenu oracleId={oracleId ?? ''} cardName={cardName} scryfallId={scryfallId} variant="button" />}

          {/* Divider after List */}
          {!hideList && (!hideDeck || !hideCollection) && <div className="bg-storm h-6 w-px" />}

          {/* Add to Deck action */}
          {!hideDeck && <AddToDeckMenu card={card} variant="button" />}

          {/* Divider after Deck */}
          {!hideDeck && !hideCollection && <div className="bg-storm h-6 w-px" />}

          {/* Add to Collection action */}
          {!hideCollection && (
            <button
              onClick={handleOpenAddModal}
              className="text-silver hover:bg-slate hover:text-pearl flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors"
              aria-label="Add to collection"
            >
              <IoAddCircleOutline className="h-4 w-4" />
              Collection
            </button>
          )}
        </div>
      </section>

      {/* Add to Collection Modal */}
      {!hideCollection && (
        <AddToCollectionModal isOpen={isAddModalOpen} onClose={handleCloseAddModal} preSelectedCard={minimalCard} setCode={setCode} />
      )}
    </>
  );
}
